import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Loader2, ChevronDown, ChevronUp, Send, RefreshCw, Zap, Plus, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PickAnalysisTriggerBtn } from '../components/PickAnalysisDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const leagueLabels = {
  la_liga: 'La Liga',
  premier_league: 'Premier',
  bundesliga: 'Bundesliga',
  serie_a: 'Serie A',
  ligue_1: 'Ligue 1',
  champions_league: 'UCL',
  mls: 'MLS',
  liga_mx: 'Liga MX',
};

export default function Picks() {
  const { user } = useAuth();
  const [picks, setPicks] = useState([]);
  const [combined, setCombined] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [channel, setChannel] = useState('all');
  const [days, setDays] = useState('7');
  const [telegramDialog, setTelegramDialog] = useState({ open: false, message: '', id: null, type: null });
  const [resolvedOpen, setResolvedOpen] = useState(false);
  
  // Elección del Día state
  const [dailyPicks, setDailyPicks] = useState(null);
  const [electionOpen, setElectionOpen] = useState(true);
  const [regeneratingDaily, setRegeneratingDaily] = useState(false);
  const [telegramPreview, setTelegramPreview] = useState(null);
  const [previewChannel, setPreviewChannel] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ days });
      if (channel !== 'all') params.append('channel', channel);
      
      // Fetch picks and combined in parallel
      const [picksRes, combinedRes] = await Promise.all([
        fetch(`${API_URL}/api/picks?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/combined${channel !== 'all' ? `?channel=${channel}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const picksData = await picksRes.json();
      const combinedData = await combinedRes.json();
      
      setPicks(picksData || []);
      setCombined(combinedData || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePicks = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/picks/generate?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(`${data.total_picks} picks, ${data.total_combined} combinadas`);
      fetchData();
    } catch (error) {
      toast.error('Error');
    } finally {
      setGenerating(false);
    }
  };

  const fetchDailyPicks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/daily-picks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDailyPicks(data);
    } catch (error) {
      console.error('Error fetching daily picks:', error);
    }
  };

  const handleRegenerateDaily = async () => {
    setRegeneratingDaily(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/daily-picks/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDailyPicks(data);
      toast.success('Elección regenerada');
    } catch (error) {
      toast.error('Error');
    } finally {
      setRegeneratingDaily(false);
    }
  };

  const generateDailyTelegramMessage = (picks, channel, combined = null) => {
    const date = dailyPicks?.date || new Date().toISOString().split('T')[0];
    const formattedDate = new Date(date).toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long'
    });
    
    let message = `🎯 *ELECCIÓN DEL DÍA - ${channel.toUpperCase()}*\n📅 ${formattedDate}\n\n`;
    
    picks.forEach((pick, idx) => {
      const matchTime = new Date(pick.match_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      message += `*${idx + 1}. ${pick.home_team} vs ${pick.away_team}*\n`;
      message += `   🏆 ${leagueLabels[pick.league] || pick.league}\n`;
      message += `   ⏰ ${matchTime}\n`;
      message += `   📌 ${pick.prediction}\n`;
      message += `   💰 Cuota: @${pick.odds?.toFixed(2)} en *${pick.bookmaker || 'Bet365'}*\n`;
      message += `   📊 Confianza: ${pick.confidence}%\n\n`;
    });
    
    if (combined && combined.picks?.length > 0) {
      message += `━━━━━━━━━━━━━━━━━━━━━\n🔥 *COMBINADA DEL DÍA*\n`;
      message += `💰 Cuota Total: @${combined.total_odds?.toFixed(2)}\n\n`;
      combined.picks.forEach((pick, idx) => {
        message += `${idx + 1}. ${pick.home_team} vs ${pick.away_team} → ${pick.prediction} @${pick.odds?.toFixed(2)}\n`;
      });
      message += `\n`;
    }
    
    message += `━━━━━━━━━━━━━━━━━━━━━\n✅ *${channel.toUpperCase()}*`;
    return message;
  };

  const showDailyTelegramPreview = (picks, channel, combined = null) => {
    const message = generateDailyTelegramMessage(picks, channel, combined);
    setTelegramPreview(message);
    setPreviewChannel(channel);
  };

  const sendDailyToTelegram = async (picks, channel, combined = null) => {
    const message = generateDailyTelegramMessage(picks, channel, combined);
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Copiado al portapapeles');
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const generateSinglePickMessage = (pick, channel) => {
    const matchTime = new Date(pick.match_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const formattedDate = new Date(pick.match_date).toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long'
    });
    
    let message = `🎯 *PICK ${channel.toUpperCase()}*\n\n`;
    message += `⚽ *${pick.home_team} vs ${pick.away_team}*\n`;
    message += `🏆 ${leagueLabels[pick.league] || pick.league}\n`;
    message += `📅 ${formattedDate} - ${matchTime}\n\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `📌 *${pick.prediction}*\n`;
    message += `💰 Cuota: @${pick.odds?.toFixed(2)} en *${pick.bookmaker || 'Bet365'}*\n`;
    message += `📊 Confianza: ${pick.confidence}%\n`;
    if (pick.value_edge) {
      message += `✨ Valor: +${pick.value_edge}%\n`;
    }
    message += `━━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `✅ *${channel.toUpperCase()}*`;
    
    return message;
  };

  const generateCombinedMessage = (combined) => {
    const formattedDate = new Date().toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long'
    });
    
    let message = `🔥 *COMBINADA DEL DÍA*\n`;
    message += `📅 ${formattedDate}\n\n`;
    message += `💰 *Cuota Total: @${combined.total_odds?.toFixed(2)}*\n\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    
    combined.picks?.forEach((pick, idx) => {
      message += `\n*${idx + 1}. ${pick.home_team} vs ${pick.away_team}*\n`;
      message += `   📌 ${pick.prediction}\n`;
      message += `   💰 @${pick.odds?.toFixed(2)}\n`;
    });
    
    message += `\n━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `🏠 Casa: *Bet365*\n`;
    message += `✅ *VIP*`;
    
    return message;
  };

  // ============== COMPLEMENTARY MARKETS ==============
  const analyzeComplementary = async (pickId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/complementary/analyze/${pickId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.has_suggestion) {
        toast.success(`Sugerencia: ${data.complementary.description}`);
        fetchData(); // Refresh to show suggestion
      } else {
        toast.info('No hay complementario sugerido para este pick');
      }
    } catch (error) {
      toast.error('Error analizando pick');
    }
  };

  const confirmComplementary = async (pickId, confirm = true) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/complementary/confirm/${pickId}?confirmed=${confirm}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(confirm ? 'Complementario confirmado' : 'Complementario rechazado');
      fetchData();
    } catch (error) {
      toast.error('Error actualizando complementario');
    }
  };

  const analyzeAllPicks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/picks/analyze-complementary`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      toast.success(`Analizados ${data.picks_analyzed} picks. ${data.suggestions_added} sugerencias añadidas.`);
      fetchData();
    } catch (error) {
      toast.error('Error analizando picks');
    }
  };

  const showSinglePickPreview = async (pick, channel) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/picks/${pick.id}/telegram-message`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTelegramPreview(data.message);
      setPreviewChannel(channel);
    } catch (error) {
      // Fallback to local generation
      const message = generateSinglePickMessage(pick, channel);
      setTelegramPreview(message);
      setPreviewChannel(channel);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDailyPicks();
  }, [channel, days]);

  const updatePickResult = async (pickId, result) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/picks/${pickId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ result: result === 'pending' ? null : result })
      });
      toast.success('Actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const updateCombinedResult = async (combinedId, result) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/combined/${combinedId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ result })
      });
      toast.success('Actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const showTelegramMessage = async (id, type) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'combined' 
        ? `${API_URL}/api/combined/${id}/telegram-message`
        : `${API_URL}/api/picks/${id}/telegram-message`;
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTelegramDialog({ open: true, message: data.message, id, type });
    } catch (error) {
      toast.error('Error');
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(telegramDialog.message);
      toast.success('¡Copiado!');
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const markSent = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = telegramDialog.type === 'combined'
        ? `${API_URL}/api/combined/${telegramDialog.id}/mark-sent`
        : `${API_URL}/api/picks/${telegramDialog.id}/mark-sent`;
      
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Marcado como enviado');
      setTelegramDialog({ open: false, message: '', id: null, type: null });
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const groupedPicks = picks.reduce((groups, pick) => {
    const date = new Date(pick.match_date).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(pick);
    return groups;
  }, {});

  // Separate pending and resolved picks
  const pendingPicks = picks.filter(p => !p.result || p.result === 'pending');
  const resolvedPicks = picks.filter(p => p.result === 'won' || p.result === 'lost');
  
  const pendingGrouped = pendingPicks.reduce((groups, pick) => {
    const date = new Date(pick.match_date).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(pick);
    return groups;
  }, {});

  const resolvedGrouped = resolvedPicks.reduce((groups, pick) => {
    const date = new Date(pick.match_date).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(pick);
    return groups;
  }, {});

  const freeCombined = combined.filter(c => c.channel === 'free');
  const vipCombined = combined.filter(c => c.channel === 'vip');

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-16 pb-20 px-3 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-4 pt-2">
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">PRONÓSTICOS</h1>
              <p className="text-xs text-zinc-400">Picks y combinadas</p>
            </div>
            <Button 
              onClick={generatePicks} 
              disabled={generating}
              className="btn-primary h-10 text-sm"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generar'}
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-800 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="1">Hoy</SelectItem>
                <SelectItem value="3">3 días</SelectItem>
                <SelectItem value="7">7 días</SelectItem>
              </SelectContent>
            </Select>

            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-800 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="free">FREE</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="stake10">STAKE 10</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* ELECCIÓN DEL DÍA - Collapsible Section */}
          {dailyPicks && !dailyPicks.error && (
            <Collapsible open={electionOpen} onOpenChange={setElectionOpen} className="mb-4">
              <div className="bg-gradient-to-r from-[#00E599]/10 to-[#EAB308]/10 border border-[#00E599]/30 rounded-xl overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors" data-testid="election-toggle">
                    <div className="text-left">
                      <h3 className="font-heading text-sm font-bold text-white">ELECCIÓN DEL DÍA</h3>
                      <p className="text-[10px] text-zinc-400 capitalize">
                        {dailyPicks?.date ? new Date(dailyPicks.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' }) : 'Hoy'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[#00E599]/20 text-[#00E599] border-0 text-[10px]">
                        {dailyPicks?.free_picks?.length || 0} FREE
                      </Badge>
                      <Badge className="bg-[#EAB308]/20 text-[#EAB308] border-0 text-[10px]">
                        {dailyPicks?.vip_picks?.length || 0} VIP
                      </Badge>
                      {electionOpen ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                    </div>
                  </button>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t border-zinc-800 p-3 space-y-3">
                    {/* Verification Warning */}
                    {dailyPicks?.verification_status && !dailyPicks.verification_status.verified && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2.5 flex items-start gap-2" data-testid="verification-warning">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-yellow-500 font-medium">Verificación Limitada</p>
                          <p className="text-[10px] text-zinc-400 mt-0.5">
                            {dailyPicks.verification_status.message}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Regenerate Button */}
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateDaily}
                        disabled={regeneratingDaily}
                        className="h-7 text-xs border-zinc-700"
                      >
                        {regeneratingDaily ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Regenerar
                      </Button>
                    </div>
                    
                    {/* FREE Channel Picks */}
                    {dailyPicks?.free_picks?.length > 0 && (
                      <div className="bg-zinc-900/50 rounded-lg p-2.5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-[#00E599]">Canal Gratuito</span>
                          <Button
                            size="sm"
                            onClick={() => sendDailyToTelegram(dailyPicks.free_picks, 'free')}
                            className="h-7 text-[10px] btn-primary"
                          >
                            <Send className="w-3 h-3 mr-1" /> Enviar Todos
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {dailyPicks.free_picks.map((pick) => (
                            <div key={pick.id} className="bg-zinc-950 rounded p-2">
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <div className="flex-1 min-w-0">
                                  <span className="text-white truncate block">{pick.home_team} vs {pick.away_team}</span>
                                  <span className="text-zinc-500 text-[10px]">{pick.prediction}</span>
                                </div>
                                <span className="text-[#00E599] font-bold ml-2">@{pick.odds?.toFixed(2)}</span>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => showSinglePickPreview(pick, 'free')}
                                className="w-full h-6 text-[10px] border-[#00E599]/30 text-[#00E599] hover:bg-[#00E599]/10"
                              >
                                <Send className="w-3 h-3 mr-1" /> Ver/Enviar Pick
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* VIP Channel Picks */}
                    {dailyPicks?.vip_picks?.length > 0 && (
                      <div className="bg-zinc-900/50 rounded-lg p-2.5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-[#EAB308]">Canal VIP</span>
                          <Button
                            size="sm"
                            onClick={() => sendDailyToTelegram(dailyPicks.vip_picks, 'vip', dailyPicks.combined)}
                            className="h-7 text-[10px] bg-[#EAB308] hover:bg-[#EAB308]/90 text-black"
                          >
                            <Send className="w-3 h-3 mr-1" /> Enviar Todos
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {dailyPicks.vip_picks.map((pick) => (
                            <div key={pick.id} className="bg-zinc-950 rounded p-2">
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <div className="flex-1 min-w-0">
                                  <span className="text-white truncate block">{pick.home_team} vs {pick.away_team}</span>
                                  <span className="text-zinc-500 text-[10px]">{pick.prediction}</span>
                                </div>
                                <span className="text-[#EAB308] font-bold ml-2">@{pick.odds?.toFixed(2)}</span>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => showSinglePickPreview(pick, 'vip')}
                                className="w-full h-6 text-[10px] border-[#EAB308]/30 text-[#EAB308] hover:bg-[#EAB308]/10"
                              >
                                <Send className="w-3 h-3 mr-1" /> Ver/Enviar Pick
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Combined */}
                    {dailyPicks?.combined && dailyPicks.combined.picks?.length > 0 && (
                      <div className="bg-[#6366F1]/10 rounded-lg p-2.5 border border-[#6366F1]/30">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-[#6366F1]">Combinada del Día</span>
                          <span className="text-[#6366F1] font-bold text-sm">@{dailyPicks.combined.total_odds?.toFixed(2)}</span>
                        </div>
                        <div className="space-y-1 mb-2">
                          {dailyPicks.combined.picks.map((pick) => (
                            <div key={pick.id || `${pick.home_team}-${pick.away_team}`} className="flex items-center justify-between text-[10px] text-zinc-400 bg-zinc-950/50 rounded px-2 py-1">
                              <span className="truncate">{pick.home_team} vs {pick.away_team}</span>
                              <span className="text-[#6366F1] ml-2">{pick.prediction} @{pick.odds?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const message = generateCombinedMessage(dailyPicks.combined);
                            setTelegramPreview(message);
                            setPreviewChannel('combinada');
                          }}
                          className="w-full h-7 text-[10px] border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10"
                        >
                          <Send className="w-3 h-3 mr-1" /> Ver/Enviar Combinada
                        </Button>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Stats */}
          <div className="grid grid-cols-5 gap-2 mb-4">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5 text-center">
              <span className="text-lg font-heading font-bold text-white">{picks.length}</span>
              <p className="text-[10px] text-zinc-500">Picks</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5 text-center">
              <span className="text-lg font-heading font-bold text-[#00E599]">
                {picks.filter(p => p.channel === 'free').length}
              </span>
              <p className="text-[10px] text-zinc-500">FREE</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5 text-center">
              <span className="text-lg font-heading font-bold text-[#EAB308]">
                {picks.filter(p => p.channel === 'vip').length}
              </span>
              <p className="text-[10px] text-zinc-500">VIP</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5 text-center">
              <span className="text-lg font-heading font-bold text-[#A855F7]">
                {picks.filter(p => p.is_stake10).length}
              </span>
              <p className="text-[10px] text-zinc-500">STAKE 10</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5 text-center">
              <span className="text-lg font-heading font-bold text-[#6366F1]">
                {combined.length}
              </span>
              <p className="text-[10px] text-zinc-500">Comb.</p>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#00E599] animate-spin" />
            </div>
          )}

          {/* Empty */}
          {!loading && picks.length === 0 && combined.length === 0 && (
            <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <p className="text-zinc-400 mb-3 text-sm">Sin pronósticos</p>
              <Button onClick={generatePicks} className="btn-primary" disabled={generating}>
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Generar con IA
              </Button>
            </div>
          )}

          {/* Picks List */}
          {!loading && picks.length > 0 && (
            <div className="space-y-6">
              {/* PENDING PICKS - Shown first */}
              {Object.keys(pendingGrouped).length > 0 && (
                <div data-testid="pending-picks-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h2 className="font-heading text-lg font-bold text-white">PENDIENTES</h2>
                      <Badge className="bg-[#EAB308]/20 text-[#EAB308] border-0 text-xs">
                        {pendingPicks.length} picks
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={analyzeAllPicks}
                      className="h-8 text-xs border-[#6366F1]/50 text-[#6366F1]"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Analizar Complementarios
                    </Button>
                  </div>
                  {Object.entries(pendingGrouped).map(([date, datePicks]) => (
                    <div key={date} className="mb-4">
                      <h3 className="font-heading text-sm font-bold text-[#00E599] mb-3 uppercase tracking-wide">
                        {date}
                      </h3>
                      <div className="space-y-3">
                        {datePicks.map((pick) => (
                          <div
                            key={pick.id}
                            className={`bg-zinc-900/50 border rounded-xl p-3 ${
                              pick.sent_to_telegram ? 'border-zinc-800 opacity-60' : 'border-[#00E599]/30'
                            }`}
                            data-testid={`pick-${pick.id}`}
                          >
                            {/* Header */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-white text-sm truncate">
                                  {pick.home_team} vs {pick.away_team}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-zinc-500">
                                    {new Date(pick.match_date).toLocaleTimeString('es-ES', { 
                                      hour: '2-digit', minute: '2-digit' 
                                    })}
                                  </span>
                                  <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] h-4">
                                    {leagueLabels[pick.league] || pick.league}
                                  </Badge>
                                </div>
                              </div>
                              <Badge className={`text-[10px] ${
                                pick.is_stake10
                                  ? 'bg-[#A855F7]/20 text-[#A855F7] border border-[#A855F7]/50'
                                  : pick.channel === 'vip' 
                                    ? 'bg-[#EAB308]/20 text-[#EAB308]' 
                                    : 'bg-zinc-700 text-zinc-300'
                              } border-0`}>
                                {pick.is_stake10 ? '💎 STAKE 10' : pick.channel === 'vip' ? 'VIP' : 'FREE'}
                              </Badge>
                            </div>
                            
                            {/* Prediction */}
                            <div className="bg-zinc-950 rounded-lg p-2.5 mb-3">
                              <span className="text-[10px] text-zinc-500">{pick.market || 'Pronóstico'}</span>
                              <p className="font-medium text-white text-sm">{pick.prediction}</p>
                              <div className="flex items-center gap-3 mt-1.5 text-xs">
                                <span className="text-[#00E599] font-bold">@{pick.odds?.toFixed(2)}</span>
                                <span className={pick.is_stake10 ? 'text-[#A855F7] font-bold' : 'text-zinc-400'}>
                                  {pick.is_stake10 ? '10u (MAX)' : `${pick.stake}u`}
                                </span>
                                <span className="text-zinc-500">{pick.bookmaker}</span>
                                {pick.value_edge && (
                                  <span className="text-emerald-400 text-[10px]">+{pick.value_edge}% edge</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Complementary Market */}
                            {pick.complementary && (
                              <div className={`rounded-lg p-2.5 mb-3 ${
                                pick.complementary.confirmed 
                                  ? 'bg-[#6366F1]/20 border border-[#6366F1]/30' 
                                  : 'bg-[#EAB308]/10 border border-[#EAB308]/20'
                              }`}>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <Zap className="w-3 h-3 text-[#EAB308]" />
                                    <span className="text-[10px] text-[#EAB308] font-medium uppercase">
                                      {pick.complementary.confirmed ? 'Potenciado' : 'Sugerencia'}
                                    </span>
                                  </div>
                                  {!pick.complementary.confirmed && (
                                    <div className="flex gap-1">
                                      <button 
                                        onClick={() => confirmComplementary(pick.id, true)}
                                        className="p-1 rounded bg-[#00E599]/20 hover:bg-[#00E599]/30"
                                      >
                                        <Check className="w-3 h-3 text-[#00E599]" />
                                      </button>
                                      <button 
                                        onClick={() => confirmComplementary(pick.id, false)}
                                        className="p-1 rounded bg-[#F43F5E]/20 hover:bg-[#F43F5E]/30"
                                      >
                                        <X className="w-3 h-3 text-[#F43F5E]" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <p className="text-sm text-white font-medium">+ {pick.complementary.description}</p>
                                  <span className="text-[#6366F1] font-bold text-sm">@{pick.complementary.odds}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-[10px]">
                                  <span className="text-[#00E599] font-bold">Combinada: @{pick.complementary.combined_odds}</span>
                                  <span className="text-zinc-500">•</span>
                                  <span className="text-zinc-400">{pick.complementary.reason}</span>
                                </div>
                              </div>
                            )}
                            
                            {/* Actions */}
                            <div className="mb-2">
                              <PickAnalysisTriggerBtn
                                pickId={pick.id}
                                matchLabel={`${pick.home_team} vs ${pick.away_team}`}
                                className="w-full h-8"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => showTelegramMessage(pick.id, 'pick')}
                                className="flex-1 btn-primary h-9 text-xs"
                              >
                                Telegram
                              </Button>
                              
                              {!pick.complementary && pick.odds < 1.50 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => analyzeComplementary(pick.id)}
                                  className="h-9 text-xs border-[#EAB308]/50 text-[#EAB308]"
                                >
                                  <Zap className="w-3 h-3 mr-1" />
                                  Potenciar
                                </Button>
                              )}
                              
                              <Button
                                size="sm"
                                variant={pick.result === 'won' ? 'default' : 'outline'}
                                onClick={() => updatePickResult(pick.id, pick.result === 'won' ? 'pending' : 'won')}
                                className={`w-10 h-9 p-0 ${pick.result === 'won' ? 'bg-[#00E599] text-black' : 'border-zinc-700'}`}
                              >
                                W
                              </Button>
                              <Button
                                size="sm"
                                variant={pick.result === 'lost' ? 'default' : 'outline'}
                                onClick={() => updatePickResult(pick.id, pick.result === 'lost' ? 'pending' : 'lost')}
                                className={`w-10 h-9 p-0 ${pick.result === 'lost' ? 'bg-[#F43F5E] text-white' : 'border-zinc-700'}`}
                              >
                                L
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty Pending State */}
              {Object.keys(pendingGrouped).length === 0 && (
                <div className="text-center py-8 bg-zinc-900/50 border border-zinc-800 rounded-xl">
                  <p className="text-zinc-400 text-sm">No hay picks pendientes</p>
                  <p className="text-zinc-500 text-xs mt-1">Todos los picks tienen resultado</p>
                </div>
              )}

              {/* RESOLVED PICKS - In collapsible */}
              {resolvedPicks.length > 0 && (
                <Collapsible open={resolvedOpen} onOpenChange={setResolvedOpen} data-testid="resolved-picks-section">
                  <div className="border border-zinc-800 rounded-xl overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button 
                        className="w-full flex items-center justify-between p-4 bg-zinc-900/50 hover:bg-zinc-900/70 transition-colors"
                        data-testid="resolved-toggle"
                      >
                        <div className="flex items-center gap-3">
                          <h2 className="font-heading text-lg font-bold text-white">RESUELTOS</h2>
                          <Badge className="bg-zinc-700 text-zinc-300 border-0 text-xs">
                            {resolvedPicks.length} picks
                          </Badge>
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-[#00E599]">{resolvedPicks.filter(p => p.result === 'won').length}W</span>
                            <span className="text-zinc-500">-</span>
                            <span className="text-[#F43F5E]">{resolvedPicks.filter(p => p.result === 'lost').length}L</span>
                          </div>
                        </div>
                        {resolvedOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                      </button>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <div className="border-t border-zinc-800 p-4 space-y-4">
                        {Object.entries(resolvedGrouped).map(([date, datePicks]) => (
                          <div key={date}>
                            <h3 className="font-heading text-sm font-bold text-zinc-400 mb-3 uppercase tracking-wide">
                              {date}
                            </h3>
                            <div className="space-y-2">
                              {datePicks.map((pick) => (
                                <div
                                  key={pick.id}
                                  className={`border rounded-lg p-3 ${
                                    pick.result === 'won' 
                                      ? 'bg-[#00E599]/10 border-[#00E599]/30' 
                                      : 'bg-[#F43F5E]/10 border-[#F43F5E]/30'
                                  }`}
                                  data-testid={`resolved-pick-${pick.id}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-white text-sm truncate">
                                        {pick.home_team} vs {pick.away_team}
                                      </p>
                                      <p className="text-[10px] text-zinc-400 mt-0.5">
                                        {pick.prediction} @{pick.odds?.toFixed(2)}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge className={pick.result === 'won' ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'}>
                                        {pick.result === 'won' ? 'W' : 'L'}
                                      </Badge>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updatePickResult(pick.id, 'pending')}
                                        className="h-7 text-[10px] text-zinc-400 hover:text-white"
                                      >
                                        Deshacer
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}
            </div>
          )}

          {/* COMBINADAS SECTION */}
          {!loading && combined.length > 0 && (
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <h2 className="font-heading text-xl font-bold text-white mb-4">
                COMBINADAS
                <span className="text-sm font-normal text-zinc-400 ml-2">
                  ({combined.length})
                </span>
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {/* FREE Combined */}
                {freeCombined.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-[#00E599]">Canal Gratuito</h3>
                    {freeCombined.map((comb) => (
                      <CombinedCard 
                        key={comb.id} 
                        combined={comb} 
                        onUpdateResult={updateCombinedResult}
                        onShowTelegram={() => showTelegramMessage(comb.id, 'combined')}
                      />
                    ))}
                  </div>
                )}
                
                {/* VIP Combined */}
                {vipCombined.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-[#EAB308]">Canal VIP</h3>
                    {vipCombined.map((comb) => (
                      <CombinedCard 
                        key={comb.id} 
                        combined={comb} 
                        onUpdateResult={updateCombinedResult}
                        onShowTelegram={() => showTelegramMessage(comb.id, 'combined')}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Telegram Dialog - Smartphone Preview */}
      <Dialog open={telegramDialog.open} onOpenChange={(open) => setTelegramDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm mx-auto p-0 overflow-hidden">
          {/* Phone Frame Header */}
          <div className="bg-[#1a1a1a] px-4 py-3 flex items-center gap-3 border-b border-zinc-800">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E599] to-[#00A86B] flex items-center justify-center">
              <span className="text-black font-bold text-xs">TIPS</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Tips</p>
              <p className="text-zinc-500 text-xs">Telegram Preview</p>
            </div>
          </div>
          
          {/* Message Content */}
          <div className="bg-[#0e1621] p-4 max-h-[60vh] overflow-y-auto">
            <div className="bg-[#182533] rounded-lg p-3 shadow-lg">
              <pre className="text-sm text-white whitespace-pre-wrap font-sans leading-relaxed" style={{fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'}}>
                {telegramDialog.message}
              </pre>
              <p className="text-[10px] text-zinc-500 text-right mt-2">
                {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="bg-[#1a1a1a] p-3 flex gap-2 border-t border-zinc-800">
            <Button onClick={copyMessage} variant="outline" className="flex-1 border-zinc-700 h-11">
              📋 Copiar
            </Button>
            <Button onClick={markSent} className="flex-1 btn-primary h-11">
              ✓ Enviado
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Pick Preview Dialog - Smartphone Style */}
      <Dialog open={!!telegramPreview} onOpenChange={() => setTelegramPreview(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm mx-auto p-0 overflow-hidden">
          {/* Phone Frame Header */}
          <div className="bg-[#1a1a1a] px-4 py-3 flex items-center gap-3 border-b border-zinc-800">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              previewChannel === 'vip' ? 'bg-gradient-to-br from-[#EAB308] to-[#B8860B]' : 'bg-gradient-to-br from-[#00E599] to-[#00A86B]'
            }`}>
              <span className="font-bold text-xs text-black">TIPS</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">
                {previewChannel === 'combinada' ? 'Combinadas' : previewChannel === 'vip' ? 'VIP Tips' : 'Free Tips'}
              </p>
              <p className="text-zinc-500 text-xs">Vista previa</p>
            </div>
          </div>
          
          {/* Message Content */}
          <div className="bg-[#0e1621] p-4 max-h-[55vh] overflow-y-auto">
            <div className={`rounded-lg p-3 shadow-lg ${
              previewChannel === 'vip' ? 'bg-[#1a1a10]' : 'bg-[#182533]'
            }`}>
              <pre className="text-sm text-white whitespace-pre-wrap font-sans leading-relaxed" style={{fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'}}>
                {telegramPreview}
              </pre>
              <p className="text-[10px] text-zinc-500 text-right mt-2">
                {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="bg-[#1a1a1a] p-3 flex gap-2 border-t border-zinc-800">
            <Button 
              variant="outline"
              onClick={() => setTelegramPreview(null)}
              className="flex-1 border-zinc-700 h-11"
            >
              Cerrar
            </Button>
            <Button 
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(telegramPreview);
                  toast.success('¡Copiado al portapapeles!');
                  setTelegramPreview(null);
                } catch (error) {
                  toast.error('Error al copiar');
                }
              }}
              className={`flex-1 h-11 ${previewChannel === 'vip' ? 'bg-[#EAB308] text-black hover:bg-[#EAB308]/90' : 'btn-primary'}`}
            >
              📋 Copiar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Combined Card Component
function CombinedCard({ combined, onUpdateResult, onShowTelegram }) {
  return (
    <div className={`bg-zinc-900/50 border rounded-xl p-3 ${
      combined.sent_to_telegram ? 'border-zinc-800 opacity-60' : 'border-[#00E599]/30'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-white text-sm">{combined.name}</h4>
        <span className="text-lg font-heading font-bold text-[#00E599]">
          @{combined.total_odds?.toFixed(2)}
        </span>
      </div>
      
      <div className="bg-zinc-950 rounded-lg p-2.5 mb-3">
        {combined.picks?.map((pick) => (
          <div key={pick.id || `${pick.home_team}-${pick.away_team}`} className="flex items-center justify-between py-1 border-b border-zinc-800 last:border-0 text-xs">
            <span className="text-zinc-300 truncate flex-1">{pick.home_team} vs {pick.away_team}</span>
            <span className="text-[#00E599] ml-2">@{pick.odds?.toFixed(2)}</span>
          </div>
        ))}
      </div>
      
      <div className="flex gap-2">
        <Button size="sm" onClick={onShowTelegram} className="flex-1 btn-primary h-9 text-xs">
          Telegram
        </Button>
        <Button
          size="sm"
          variant={combined.result === 'won' ? 'default' : 'outline'}
          onClick={() => onUpdateResult(combined.id, combined.result === 'won' ? null : 'won')}
          className={`w-10 h-9 p-0 ${combined.result === 'won' ? 'bg-[#00E599] text-black' : 'border-zinc-700'}`}
        >
          W
        </Button>
        <Button
          size="sm"
          variant={combined.result === 'lost' ? 'default' : 'outline'}
          onClick={() => onUpdateResult(combined.id, combined.result === 'lost' ? null : 'lost')}
          className={`w-10 h-9 p-0 ${combined.result === 'lost' ? 'bg-[#F43F5E] text-white' : 'border-zinc-700'}`}
        >
          L
        </Button>
      </div>
    </div>
  );
}
